import layoutSvg from './icons/layout.svg?raw';
import lightningSvg from './icons/lightning.svg?raw';
import packageSvg from './icons/package.svg?raw';

class Homepage extends HTMLElement {
    connectedCallback() {
        this.render();
    }

    render() {
        this.innerHTML = `
            <style>
                .homepage-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 3rem 2rem;
                }

                .homepage-hero {
                    text-align: center;
                    margin-bottom: 4rem;
                    animation: fadeIn 600ms ease-out;
                }

                .homepage-hero h1 {
                    font-size: 3rem;
                    font-weight: 700;
                    color: #1e1e1e;
                    margin-bottom: 1rem;
                    line-height: 1.2;
                }

                .homepage-hero p {
                    font-size: 1.25rem;
                    color: #626262;
                    max-width: 700px;
                    margin: 0 auto;
                    line-height: 1.6;
                }

                .homepage-features {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 2rem;
                    margin-bottom: 3rem;
                }

                .feature-card {
                    background: white;
                    border: 1px solid #eaecf0;
                    border-radius: 0.5rem;
                    padding: 2rem;
                    transition: all 200ms ease;
                    animation: fadeInUp 600ms ease-out backwards;
                    text-decoration: none;
                    color: inherit;
                    display: block;
                }

                .feature-card:nth-child(1) { animation-delay: 100ms; }
                .feature-card:nth-child(2) { animation-delay: 200ms; }
                .feature-card:nth-child(3) { animation-delay: 300ms; }

                .feature-card:hover {
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    transform: translateY(-2px);
                }

                .feature-icon {
                    width: 48px;
                    height: 48px;
                    background: #e4ecfe;
                    border-radius: 0.5rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 1rem;
                }

                .feature-icon svg {
                    width: 24px;
                    height: 24px;
                    color: #0c4c98;
                }

                .feature-card h3 {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #1e1e1e;
                    margin-bottom: 0.75rem;
                }

                .feature-card p {
                    color: #626262;
                    line-height: 1.6;
                    margin: 0;
                }

                .homepage-cta {
                    background: linear-gradient(135deg, #0c4c98 0%, #1d70d0 100%);
                    border-radius: 0.75rem;
                    padding: 3rem;
                    text-align: center;
                    color: white;
                    animation: fadeIn 600ms ease-out 400ms backwards;
                }

                .homepage-cta h2 {
                    font-size: 2rem;
                    font-weight: 600;
                    margin-bottom: 1rem;
                }

                .homepage-cta p {
                    font-size: 1.125rem;
                    margin-bottom: 2rem;
                    opacity: 0.95;
                }

                .nav-links {
                    display: flex;
                    gap: 1rem;
                    justify-content: center;
                    flex-wrap: wrap;
                }

                .nav-link {
                    background: white;
                    color: #0c4c98;
                    padding: 0.75rem 1.5rem;
                    border-radius: 0.375rem;
                    text-decoration: none;
                    font-weight: 500;
                    transition: all 200ms ease;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .nav-link:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }

                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @media (max-width: 768px) {
                    .homepage-hero h1 {
                        font-size: 2rem;
                    }

                    .homepage-hero p {
                        font-size: 1rem;
                    }

                    .homepage-features {
                        grid-template-columns: 1fr;
                    }

                    .homepage-cta {
                        padding: 2rem 1.5rem;
                    }

                    .homepage-cta h2 {
                        font-size: 1.5rem;
                    }
                }
            </style>
            <div class="homepage-container">
                <div class="homepage-hero" data-testid="homepage-welcome"">
                    <h1>Welcome to Workload Frontend</h1>
                    <p>
                        A modern, single-spa powered sandbox for developing
                        PartsTrader applications. Built with Web Components and
                        Microfrontend architecture for maximum flexibility and scalability.
                    </p>
                </div>

                <div class="homepage-features">
                    <a class="feature-card" href="https://frontend.ptml.com/architecture/overview" target="_blank" rel="noopener noreferrer">
                        <div class="feature-icon" data-testid="microfrontend-architecture-title">
                            ${layoutSvg}
                        </div>
                        <h3>Microfrontend Architecture</h3>
                        <p>
                            Powered by single-spa, enabling independent deployment and development
                            of features with import map overrides for seamless local development.
                        </p>
                    </a>

                    <div class="feature-card">
                        <div class="feature-icon" data-testid="web-components-title">
                            ${lightningSvg}
                        </div>
                        <h3>Web Components</h3>
                        <p>
                            Lightweight, framework-agnostic components for UI elements that don't
                            require the overhead of full microfrontends. Fast and efficient.
                        </p>
                    </div>

                    <div class="feature-card">
                        <div class="feature-icon" data-testid="modular-design-title">
                            ${packageSvg}
                        </div>
                        <h3>Modular Design</h3>
                        <p>
                            Each feature area can be developed, tested,
                            and deployed independently without affecting other parts of the system.
                        </p>
                    </div>
                </div>

                <div class="homepage-cta">
                    <h2>Get Started</h2>
                    <p>Choose a section to manage configurations and settings.</p>
                    <div class="nav-links">
                        <a href="/settings" class="nav-link">
                            ${packageSvg}
                            Settings
                        </a>
                    </div>
                </div>
            </div>
        `;

        this.attachEventListeners();
    }

    attachEventListeners() {
        const links = this.querySelectorAll('a[href]');
        links.forEach((link) => {
            link.addEventListener('click', (event) => {
                const href = (link as HTMLAnchorElement).getAttribute('href');
                // Don't prevent default for external links
                if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
                    return;
                }

                event.preventDefault();
                if (href) {
                    window.history.pushState({}, '', href);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                }
            });
        });
    }
}

// Register the custom element
customElements.define('home-page', Homepage);

export { Homepage };
